import React from 'react';
import styled, { css } from 'styled-components';

import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';

import { colors, mixins, fonts, media } from '../../../css';

/**
 * DatePickerField Styles
 *
 * Styled*Classes - These objects are used to enforce consistency with the classnames that are passed
 * in as props and subsequently passed down to the @material-ui components themselves
 * Styled* - represents the `styled`-tagged version of the @material-ui component
 *
 * Note that the props used inside the `styled`-tagged functions are passed into the component in the
 * DatePickerField component file.
 */

export const StyledFormControlClasses = {
  root: 'DatePickerField-formcontrol-root'
};

const spreadFormControl = ({ flexGrow, ...rest }) => <FormControl {...rest} />;
export const StyledFormControl = styled(spreadFormControl)`
  &&.${({ classes }) => classes.root} {
    width: 100%;
    min-height: 100%;
  }
`;

export const StyledInputClasses = {
  root: 'DatePickerField-root',
  focused: 'DatePickerField-input-focused',
  input: 'DatePickerField-input',
  inputType: 'DatePickerField-input'
};

const inputBorderTypes = {
  default: css`
    border: 1px solid ${colors.colorGrey04};

    &&.${({ classes }) => classes.focused} {
      box-shadow: inset 2px 2px 0 0 ${colors.colorBlue}, inset -2px -2px 0 0 ${colors.colorBlue};
    }
  `,
  none: css`
    border: none;
    &&.${({ classes }) => classes.focused} {
      border: none;
    }
  `,
  search: css`
    border: 1px solid ${colors.colorGrey04};
    border-width: 0 1px 1px 0;

    &&.${({ classes }) => classes.focused} {
      border-width: 0px;
      ${media.greaterThan('570px')`
        border-width: 0px 1px 0px 0px;
      `}
      box-shadow: inset 2px 2px 0 0 ${colors.colorBlue}, inset -2px -2px 0 0 ${colors.colorBlue};
    }
  `
};

/** Break this out into it's own pointer so it doesn't SCSS formatting in VSCode */
const spreadInput = ({ borderTheme, ...rest }) => <Input {...rest} />;
export const StyledInput = styled(spreadInput)`
  &&.${({ classes }) => classes.root} {
    /* Element */
    background-color: #ffffff;
    caret-color: ${colors.colorBlue};
    height: 80px;
    width: 100%;
    margin: 0;
    padding: 0 0 0 ${mixins.pxToRem(20)};
    ${media.greaterThan('370px')`padding: 0 0 0 ${mixins.pxToRem(35)};`};

    ${({ borderTheme }) => inputBorderTypes[borderTheme]};
  }

  && .${({ classes }) => classes.input} {
    /* Typopgraphy */
    color: ${colors.colorGrey};
    font-family: ${fonts.fontMedium};
    font-size: ${mixins.pxToRem(15)};
    ${media.greaterThan('370px')`font-size: ${mixins.pxToRem(18)};`}
    caret-color: ${colors.colorBlue};
    padding: ${mixins.pxToRem(16)} 0 ${mixins.pxToRem(7)};

    /* Element */
    position: relative;
    width: 100%;
    min-height: 100%;
    margin: 0;

    &:hover {
      ${(props) => !props.disabled && `cursor: pointer;`};
    }

    /** Go away, spinners **/
    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* some hacky stuff here to disable autofill styles. */
    ::-webkit-autofill,
    ::-webkit-autofill:hover,
    ::-webkit-autofill:focus {
      display: none;
    }

    /** Go away, IE11 */
    ::-ms-clear,
    ::-ms-reveal {
      display: none;
      width: 0;
      height: 0;
    }
  }
`;

export const StyledLabelClasses = {
  root: 'DatePickerField-label',
  shrink: 'DatePickerField-label-shrink'
};

const spreadLabel = ({ hasIcon, height, ...rest }) => <InputLabel {...rest} />;
export const StyledLabel = styled(spreadLabel)`
  &&.${({ classes }) => classes.root} {
    /* Typopgraphy */
    color: ${colors.colorGrey03};
    font-family: ${fonts.fontRegular};
    font-size: ${mixins.pxToRem(16)};
    ${media.greaterThan('370px')`font-size: ${mixins.pxToRem(18)}`};
    top: 50%;
    left: 0;
    transform: translate(0, -50%) scale(1);
    transform-origin: top left;
    pointer-events: none;

    /* Element */
    margin-left: ${mixins.pxToRem(20)};
    ${media.greaterThan('370px')`margin-left: ${mixins.pxToRem(36)};`};
    z-index: 1;
  }

  &&.${({ classes }) => classes.shrink} {
    font-size: ${mixins.pxToRem(16)};
    color: ${colors.colorBlue};
    left: 0;
    margin: ${mixins.pxToRem(18)} ${mixins.pxToRem(20)};
    ${media.greaterThan('370px')`margin: ${mixins.pxToRem(18)} 0 0 ${mixins.pxToRem(36)};`};
    transform-origin: top left;
    transition: all 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    transition-property: transform, top;

    ${({ height }) => {
      if (height < 70) {
        return `
        top: 0px;
        transform: translate(0, 0) scale(0.65);
        `;
      }

      return `
      top: 5%;
      transform: translate(0, -5%) scale(0.75);`;
    }};
  }
`;

export const StyledContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;
